import React, { useState, useMemo } from 'react';
import {
  Container,
  Stack,
  Group,
  Button,
  Text,
  Card,
  LoadingOverlay,
  Alert,
  Modal,
  Title,
  Divider,
  Textarea,
} from '@mantine/core';

import { IconBook, IconMail, IconPencil, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { notifications } from '@mantine/notifications';

// Extend dayjs with UTC plugin
dayjs.extend(utc);

import {
  useFilteredJournals,
  useJournal,
} from '../../../global/entities/Journal/queries/useJournals';
import {
  usePatientJournalEntries,
  useDeletePatientJournalEntry,
} from '../../../global/entities/Journal/queries/useJournalEntries';
import {
  IJournal,
  IJournalEntry,
} from '../../../global/entities/Journal/Journal';
import { JournalCategoryStep } from './JournalCategoryStep';
import { inject, observer } from 'mobx-react';
import { PatientStore } from '../../../global/Store/PatientStore';
import { useSessionProvider } from '../../../global/provider/sessionContext';
import { JournalsApi } from '../../../global/apis/JournalsApi/JournalsApi';
import PatientJournalSettings from '../../HcpPatientDashboard/Form/PatientJournalSettings.tsx';
import { MobileJournalLayout } from './mobile/MobileJournalLayout';

// Extended interface for demo purposes
interface IJournalEntryWithSummary extends IJournalEntry {
  summary?: string;
  details?: string;
}

interface PatientJournalListProps {
  patientStore?: PatientStore;
  isHcpView?: boolean;
}

export const PatientJournalList: React.FC<PatientJournalListProps> = inject(
  'patientStore',
)(
  observer(
    ({
      patientStore,
      isHcpView = false, // Default to false for patient view
    }) => {
      const { t } = useTranslation();
      const navigate = useNavigate();
      const { i18n } = useTranslation();
      const currentLang = i18n.language as 'en' | 'fr';

      const { currentUser } = useSessionProvider();

      // Add media query hooks to detect small portrait screens
      const isSmallScreen = useMediaQuery('(max-width: 767px)');
      const isPortrait = useMediaQuery('(orientation: portrait)');
      const isTouchDevice = useMediaQuery('(pointer: coarse)');
      const isMobilePortrait = isSmallScreen && isPortrait && isTouchDevice;

      const [activeTab, setActiveTab] = useState<string | null>(null);
      const [selectedDate, setSelectedDate] = useState<Date>(new Date());
      const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
      const [sendingReminder, setSendingReminder] = useState<boolean>(false);
      const [viewModalOpened, setViewModalOpened] = useState<boolean>(false);
      const [selectedEntry, setSelectedEntry] =
        useState<IJournalEntryWithSummary | null>(null);
      const [reminderModalOpened, setReminderModalOpened] =
        useState<boolean>(false);
      const [personalizedMessage, setPersonalizedMessage] =
        useState<string>('');
      const [deleteConfirmOpened, setDeleteConfirmOpened] =
        useState<boolean>(false);

      const patientId = parseInt(
        patientStore?.patientId || currentUser?.patientID || '0',
      );

      // Fetch available journals and patient entries
      const { data: journals = [], isLoading: journalsLoading } =
        useFilteredJournals(
          patientStore?.therapeuticIndicationId
            ? parseInt(patientStore.therapeuticIndicationId)
            : undefined,
          patientStore?.sexId ? parseInt(patientStore.sexId) : undefined,
        );
      const { data: entries = [], isLoading: entriesLoading } =
        usePatientJournalEntries(patientId);
      const deleteEntryMutation = useDeletePatientJournalEntry();

      const currentJournalId = activeTab
        ? parseInt(activeTab)
        : journals[0]?.id || 0;

      // Fetch detailed journal data for the current active journal (for viewing entries)
      const { data: currentJournalData } = useJournal(currentJournalId);
      const isLoading = journalsLoading || entriesLoading;

      // Combine real entries with mock entries for demonstration
      const allEntries: IJournalEntryWithSummary[] = [...entries];

      // Set default active tab when journals load
      React.useEffect(() => {
        if (journals.length > 0 && !activeTab)
          setActiveTab(journals[0].id.toString());
      }, [journals, activeTab]);

      const getJournalEntries = (journalId: number) => {
        return allEntries.filter((entry) => entry.journalID === journalId);
      };

      const handleSendReminder = async () => {
        try {
          setSendingReminder(true);
          await JournalsApi.sendReminder(
            patientId,
            currentJournalId,
            personalizedMessage,
          );
          notifications.show({
            title: t('journal.reminderSent', 'Reminder sent successfully'),
            message: t(
              'journal.reminderSentMessage',
              'The patient has been notified to complete their journal.',
            ),
            color: 'green',
          });
          setReminderModalOpened(false);
          setPersonalizedMessage('');
        } catch (error) {
          console.error('Failed to send journal reminder:', error);
          notifications.show({
            title: t('journal.reminderFailed', 'Failed to send reminder'),
            message: t(
              'journal.reminderFailedMessage',
              'There was an error sending the reminder.',
            ),
            color: 'red',
          });
        } finally {
          setSendingReminder(false);
        }
      };

      const handleViewEntry = (entry: IJournalEntryWithSummary) => {
        setSelectedEntry(entry);
        setViewModalOpened(true);
      };

      const generateCalendarDays = () => {
        const startOfMonth = dayjs(currentMonth).startOf('month');
        const endOfMonth = dayjs(currentMonth).endOf('month');
        const startOfCalendar = startOfMonth.startOf('week');
        const endOfCalendar = endOfMonth.endOf('week');

        const days = [];
        let currentDay = startOfCalendar;

        while (
          currentDay.isBefore(endOfCalendar) ||
          currentDay.isSame(endOfCalendar, 'day')
        ) {
          days.push(currentDay.toDate());
          currentDay = currentDay.add(1, 'day');
        }

        return days;
      };

      const calendarDays = useMemo(
        () => generateCalendarDays(),
        [currentMonth],
      );

      if (isLoading) {
        return (
          <Container size="lg" py="md">
            <LoadingOverlay visible />
          </Container>
        );
      }

      if (journals.length === 0) {
        return (
          <Container size="lg" py="md">
            <Alert icon={<IconBook size={16} />}>
              {t(
                'journal.noJournalsAvailable',
                'No journals are currently available.',
              )}
            </Alert>
          </Container>
        );
      }

      // Render the journal entry view modal
      const renderEntryViewModal = () => {
        if (!selectedEntry || !currentJournalData) return null;

        // Group responses by category
        const responsesMap = new Map();
        selectedEntry.responses.forEach((response) => {
          responsesMap.set(response.journalQuestionID, response);
        });

        // Filter categories that have answered questions
        const categoriesWithAnswers = currentJournalData.categories
          ?.map((category) => {
            const questionsWithAnswers = category.questions.filter(
              (question) => {
                const response = responsesMap.get(question.id);
                return (
                  response &&
                  !response.isSkipped &&
                  response.responseValue &&
                  response.responseValue.trim() !== ''
                );
              },
            );

            return {
              ...category,
              questions: questionsWithAnswers,
            };
          })
          .filter((category) => category.questions.length > 0)
          .sort((a, b) => a.order - b.order);

        return (
          <Modal
            opened={viewModalOpened}
            onClose={() => setViewModalOpened(false)}
            title={
              <Title order={3}>
                {currentLang === 'fr'
                  ? currentJournalData.nameFr
                  : currentJournalData.nameEn}
              </Title>
            }
            size={isMobilePortrait ? '100%' : 'xl'}
            fullScreen={isMobilePortrait}
          >
            <Stack gap="lg">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t('journal.entryDate', 'Entry Date')}:{' '}
                  {dayjs
                    .utc(selectedEntry.entryDate)
                    .local()
                    .format('MMMM D, YYYY [–] HH:mm')}
                </Text>
                {!isHcpView && (
                  <Group gap="xs">
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconPencil size={14} />}
                      onClick={() => {
                        setViewModalOpened(false);
                        navigate(
                          `/patientDashboard/journals/${currentJournalId}/entry/${selectedEntry.id}`,
                        );
                      }}
                    >
                      {t('journal.modify', 'Modify')}
                    </Button>
                    <Button
                      variant="light"
                      color="red"
                      size="xs"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => setDeleteConfirmOpened(true)}
                    >
                      {t('common.delete', 'Delete')}
                    </Button>
                  </Group>
                )}
              </Group>

              <Divider />

              {/* Categories and Questions using existing components */}
              {categoriesWithAnswers?.map((category) => (
                <JournalCategoryStep
                  key={category.id}
                  category={category}
                  responses={category.questions.map((question) => {
                    const response = responsesMap.get(question.id);
                    return (
                      response || {
                        journalQuestionID: question.id,
                        responseValue: '',
                        subResponseValue: '',
                        subResponseMandatory: false,
                        isSkipped: false,
                        responseDate: new Date().toISOString(),
                      }
                    );
                  })}
                  onResponsesChange={() => {}} // No-op since it's read-only
                  readOnly={true}
                />
              ))}

              {/* Notes if available */}
              {selectedEntry.notes && (
                <Card withBorder p="md">
                  <Stack gap="sm">
                    <Title order={5}>{t('journal.notes', 'Notes')}</Title>
                    <Text size="sm">{selectedEntry.notes}</Text>
                  </Stack>
                </Card>
              )}

              {/* Delete confirmation modal */}
              <Modal
                opened={deleteConfirmOpened}
                onClose={() => setDeleteConfirmOpened(false)}
                title={t('journal.deleteEntryTitle', 'Delete Journal Entry')}
                size="sm"
                centered
              >
                <Stack gap="md">
                  <Text size="sm">
                    {t(
                      'journal.deleteEntryConfirmation',
                      'Are you sure you want to delete this journal entry? This action cannot be undone.',
                    )}
                  </Text>
                  <Group justify="flex-end" gap="md">
                    <Button
                      variant="subtle"
                      onClick={() => setDeleteConfirmOpened(false)}
                    >
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                      color="red"
                      leftSection={<IconTrash size={16} />}
                      loading={deleteEntryMutation.isPending}
                      onClick={async () => {
                        try {
                          await deleteEntryMutation.mutateAsync(
                            selectedEntry.id,
                          );
                          setDeleteConfirmOpened(false);
                          setViewModalOpened(false);
                          notifications.show({
                            title: t('journal.success', 'Success'),
                            message: t(
                              'journal.entryDeleted',
                              'Journal entry deleted successfully.',
                            ),
                            color: 'green',
                          });
                        } catch (error) {
                          notifications.show({
                            title: t('journal.error', 'Error'),
                            message: t(
                              'journal.deleteEntryFailed',
                              'Failed to delete journal entry.',
                            ),
                            color: 'red',
                          });
                        }
                      }}
                    >
                      {t('common.delete', 'Delete')}
                    </Button>
                  </Group>
                </Stack>
              </Modal>
            </Stack>
          </Modal>
        );
      };

      // Render the journal reminder modal
      const renderReminderModal = () => {
        return (
          <Modal
            opened={reminderModalOpened}
            onClose={() => {
              setReminderModalOpened(false);
              setPersonalizedMessage('');
            }}
            title={
              <Title order={3}>
                {t(
                  'journal.sendPersonalizedReminder',
                  'Send Personalized Reminder',
                )}
              </Title>
            }
            size="md"
          >
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                {t(
                  'journal.reminderModalDescription',
                  'Send a personalized reminder to the patient to complete their journal entry. You can add a custom message that will be included in the email.',
                )}
              </Text>

              <Textarea
                label={t(
                  'journal.personalizedMessage',
                  'Personalized Message (Optional)',
                )}
                placeholder={t(
                  'journal.personalizedMessagePlaceholder',
                  'Enter a personalized message for the patient...',
                )}
                value={personalizedMessage}
                onChange={(event) =>
                  setPersonalizedMessage(event.currentTarget.value)
                }
                rows={4}
                maxLength={500}
              />

              <Text size="xs" c="dimmed">
                {personalizedMessage.length}/500{' '}
                {t('journal.characters', 'characters')}
              </Text>

              <Group justify="flex-end" gap="md">
                <Button
                  variant="subtle"
                  onClick={() => {
                    setReminderModalOpened(false);
                    setPersonalizedMessage('');
                  }}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  leftSection={<IconMail size={16} />}
                  onClick={handleSendReminder}
                  loading={sendingReminder}
                >
                  {t('journal.sendReminder', 'Send Reminder')}
                </Button>
              </Group>
            </Stack>
          </Modal>
        );
      };

      return (
        <Container size="lg" py={isMobilePortrait ? 'xs' : 'md'}>
          <Stack gap={isMobilePortrait ? 'sm' : 'xl'}>
            {/* Entry View Modal */}
            {renderEntryViewModal()}

            {/* Reminder Modal */}
            {renderReminderModal()}

            <MobileJournalLayout
              journals={journals}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              entries={getJournalEntries(currentJournalId)}
              calendarDays={calendarDays}
              currentMonth={currentMonth}
              selectedDate={selectedDate}
              onMonthChange={setCurrentMonth}
              onDateSelect={setSelectedDate}
              onViewEntry={handleViewEntry}
              onAddEntry={(dateString) => {
                navigate(
                  `/patientDashboard/journals/${currentJournalId}/new-entry/${dateString}`,
                );
              }}
              isHcpView={isHcpView}
              onSendReminder={() => setReminderModalOpened(true)}
              maxDots={isMobilePortrait ? 3 : 5}
            />

            {/* Patient Journal Settings - Only visible in HCP view */}
            {isHcpView && (
              <PatientJournalSettings
                journalId={currentJournalId}
                journalName={
                  journals.find((j) => j.id === currentJournalId)?.name || ''
                }
              />
            )}
          </Stack>
        </Container>
      );
    },
  ),
);
